import { AUDIT_EVENT_TYPES } from '@malaberg/shared';
/**
 * AuditEventFilters — T29.
 *
 * Self-contained controlled filter panel for the audit log.
 * Emits a complete filters object via onChange whenever any field changes.
 *
 * Fields:
 * - userId (TextInput — uuid)
 * - actingUserId (TextInput — uuid)
 * - eventType (Select from AUDIT_EVENT_TYPES)
 * - entityType (Select from fixed list)
 * - dateFrom, dateTo (Mantine DateInput)
 */
import { Group, Select, Stack, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';

export interface AuditFilters {
  userId?: string;
  actingUserId?: string;
  eventType?: string;
  entityType?: string;
  dateFrom?: string;
  dateTo?: string;
}

const ENTITY_TYPE_OPTIONS = [
  { value: 'user', label: 'user' },
  { value: 'product', label: 'product' },
  { value: 'source_pack_file', label: 'source_pack_file' },
  { value: 'system_prompt', label: 'system_prompt' },
  { value: 'parameter_library_entry', label: 'parameter_library_entry' },
  { value: 'run', label: 'run' },
];

const EVENT_TYPE_OPTIONS = AUDIT_EVENT_TYPES.map((t) => ({ value: t, label: t }));

interface Props {
  filters: AuditFilters;
  onChange: (filters: AuditFilters) => void;
}

export function AuditEventFilters({ filters, onChange }: Props) {
  function update(patch: Partial<AuditFilters>) {
    onChange({ ...filters, ...patch });
  }

  function handleDateFrom(date: Date | null) {
    update({ dateFrom: date ? date.toISOString().slice(0, 10) : undefined });
  }

  function handleDateTo(date: Date | null) {
    update({ dateTo: date ? date.toISOString().slice(0, 10) : undefined });
  }

  return (
    <Stack gap="sm" data-testid="audit-event-filters">
      <Group grow>
        <TextInput
          label="User ID"
          placeholder="UUID"
          value={filters.userId ?? ''}
          onChange={(e) => update({ userId: e.currentTarget.value || undefined })}
          data-testid="filter-user-id"
        />
        <TextInput
          label="Acting user ID"
          placeholder="UUID"
          value={filters.actingUserId ?? ''}
          onChange={(e) => update({ actingUserId: e.currentTarget.value || undefined })}
          data-testid="filter-acting-user-id"
        />
      </Group>

      <Group grow>
        <Select
          label="Event type"
          placeholder="All event types"
          data={EVENT_TYPE_OPTIONS}
          value={filters.eventType ?? null}
          onChange={(val) => update({ eventType: val ?? undefined })}
          clearable
          searchable
          data-testid="filter-event-type"
        />
        <Select
          label="Entity type"
          placeholder="All entity types"
          data={ENTITY_TYPE_OPTIONS}
          value={filters.entityType ?? null}
          onChange={(val) => update({ entityType: val ?? undefined })}
          clearable
          data-testid="filter-entity-type"
        />
      </Group>

      <Group grow>
        <DateInput
          label="Date from"
          placeholder="Pick date"
          value={filters.dateFrom ? new Date(filters.dateFrom) : null}
          onChange={handleDateFrom}
          clearable
          data-testid="filter-date-from"
        />
        <DateInput
          label="Date to"
          placeholder="Pick date"
          value={filters.dateTo ? new Date(filters.dateTo) : null}
          onChange={handleDateTo}
          clearable
          data-testid="filter-date-to"
        />
      </Group>
    </Stack>
  );
}
